import { LOCAL_STORAGE } from '@mimir/web/util/constants';
import type { FuzzyValue } from '@mimir/common/util/fuzzyNumbers';
import { isInstanceOfFraction } from '@mimir/common/util/fractionType';

const MAX_ENTRIES = 10;

interface MarkPointsCacheEntry {
  itemId: string;
  inPoint: FuzzyValue | null;
  outPoint: FuzzyValue | null;
  updatedAt: number;
}


export function saveMarkPoints(itemId: string, inPoint: FuzzyValue | null, outPoint: FuzzyValue | null): void {
  const entries = loadEntries();
  const existingIndex = entries.findIndex(e => e.itemId === itemId);

  if (inPoint === null && outPoint === null) {
    if (existingIndex !== -1) {
      entries.splice(existingIndex, 1);
      saveEntries(entries);
    }
    return;
  }

  if (existingIndex !== -1) {
    entries.splice(existingIndex, 1);
  }

  entries.push({
    itemId,
    inPoint,
    outPoint,
    updatedAt: Date.now(),
  });

  while (entries.length > MAX_ENTRIES) {
    evictOldest(entries);
  }

  saveEntries(entries);
}

export function getMarkPoints(itemId: string): { inPoint: FuzzyValue | null; outPoint: FuzzyValue | null } | null {
  const entry = loadEntries().find(e => e.itemId === itemId);
  if (entry === undefined) return null;
  return { inPoint: entry.inPoint, outPoint: entry.outPoint };
}

export function clearMarkPoints(itemId: string): void {
  saveMarkPoints(itemId, null, null);
}

function evictOldest(entries: MarkPointsCacheEntry[]): void {
  let oldestIndex = 0;
  for (let i = 1; i < entries.length; i++) {
    if (entries[i].updatedAt < entries[oldestIndex].updatedAt) {
      oldestIndex = i;
    }
  }
  entries.splice(oldestIndex, 1);
}

function loadEntries(): MarkPointsCacheEntry[] {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE.MARK_POINTS_CACHE);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidEntry);
  } catch {
    return [];
  }
}

function saveEntries(entries: MarkPointsCacheEntry[]): void {
  try {
    localStorage.setItem(LOCAL_STORAGE.MARK_POINTS_CACHE, JSON.stringify(entries));
  } catch {
    // localStorage can throw in private-browsing mode or when quota is exceeded.
    // The cache is best-effort; swallowing here is preferable to crashing the timeline UI.
    console.warn('Failed to save mark points cache to localStorage.');
  }
}

function isValidFuzzyValue(value: unknown): value is FuzzyValue {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.approximate !== 'number') return false;
  if (v.precise !== undefined && !isInstanceOfFraction(v.precise)) return false;
  return true;
}

function isValidEntry(entry: unknown): entry is MarkPointsCacheEntry {
  if (typeof entry !== 'object' || entry === null) return false;
  const e = entry as Record<string, unknown>;
  return typeof e.itemId === 'string' &&
    (e.inPoint === null || isValidFuzzyValue(e.inPoint)) &&
    (e.outPoint === null || isValidFuzzyValue(e.outPoint)) &&
    typeof e.updatedAt === 'number';
}
